import { useCallback, useEffect, useMemo } from 'react';
import { FCX, ID, SelectSingle } from '@datassential/platform-ui-lib';
import { useHomeCommonData } from '@apiHooks/useHomeCommonData';
import useLocalStorageState from '@hooks/useLocalStorageState';

interface HomeLnrCategoryResult {
    selectedCategoryId: ID;
    setSelectedCategoryId: (value: ID) => void;
    selectedCategoryName: string;
    options: Array<{ id: ID; title: string }>;
}

export const ALL_CATEGORIES_TITLE = 'All Categories';

export function useHomeLnrCategory(): HomeLnrCategoryResult {
    const { data } = useHomeCommonData();
    const categoryIdValueConverter = useCallback(
        (value: string | null): ID => {
            if (value === null || value === 'null') return 0 as ID;
            const num = Number(value);
            if (isNaN(num)) return 0 as ID;
            return num as ID;
        },
        [],
    );
    const [selectedCategoryId, setSelectedCategoryId] = useLocalStorageState<ID>(
        'home-category-lens-category',
        0 as ID,
        categoryIdValueConverter
    );

    const options = useMemo(
        () => [
            { id: 0 as ID, title: ALL_CATEGORIES_TITLE },
            ...[...(data?.lrCategories ?? [])].sort(
                (a, b) => (a.title ?? '').localeCompare(b.title ?? '')
            ),
        ],
        [data]
    );

    useEffect(() => {
        if (options.length > 1 && !options.some(c => c.id === selectedCategoryId)) {
            setSelectedCategoryId(0 as ID);
        }
    }, [options, selectedCategoryId, setSelectedCategoryId]);

    const selectedCategoryName = useMemo(
        () => options.find(c => c.id === selectedCategoryId)?.title ?? '',
        [options, selectedCategoryId]
    );

    return {
        selectedCategoryId,
        setSelectedCategoryId,
        selectedCategoryName,
        options,
    };
}

interface HomeLnrCategoryProps {
    value: ID;
    setValue: (value: ID) => void;
    options: Array<{ id: ID; title: string }>;
}

const HomeLnrCategory: FCX<HomeLnrCategoryProps> = ({
    value,
    setValue,
    options,
    className,
    testId = 'selectHomeLnrCategory',
}) => {
    if (options.length === 0) return null;

    return (
        <SelectSingle
            className={`HomeLnrCategory ${className || ''}`}
            value={value}
            setValue={setValue}
            options={options}
            dropdownProps={{
                style: {
                    maxHeight: 300,
                    overflowX: 'hidden',
                },
                className: 'no-scroll-override',
                place: 'bottom-start',
            }}
            fieldProps={{
                style: { minWidth: 200 },
            }}
            hasSearch
            testId={testId}
        />
    );
};

export default HomeLnrCategory;
